import { AlertCircle } from 'lucide-react';

/**
 * Scanning tool options available for accessibility and quality auditing
 */
export type ScanTool = 'axe' | 'lighthouse' | 'pa11y';

interface ToolSelectorProps {
  selectedTools: ScanTool[];
  onChange: (tools: ScanTool[]) => void;
  disabled?: boolean;
}

interface ToolInfo {
  id: ScanTool;
  name: string;
  description: string;
  badgeColor: string;
  badgeBgColor: string;
}

const TOOLS: ToolInfo[] = [
  {
    id: 'axe',
    name: 'axe',
    description: 'WCAG accessibility testing with detailed issue reporting',
    badgeColor: 'text-white',
    badgeBgColor: 'bg-[#6366f1]', // Purple for axe
  },
  {
    id: 'lighthouse',
    name: 'Lighthouse',
    // Performance/Core Web Vitals come from the "Performance" scan type (via
    // PageSpeed Insights), not this tool — so the label reflects what the
    // Lighthouse tool actually produces here: category scores.
    description: 'SEO, best-practices & accessibility scores (Google Lighthouse)',
    badgeColor: 'text-white',
    badgeBgColor: 'bg-[#f97316]', // Orange for Lighthouse
  },
  // pa11y (HTML CodeSniffer) removed 2026-07-10: real axe-core already covers
  // everything HTMLCS found on WP sites; HTMLCS only added noise (WCAG 4.1.1,
  // obsolete in 2.2) and duplicate 1.3.1. Product decision: drop it.
];

export function ToolSelector({ selectedTools, onChange, disabled = false }: ToolSelectorProps) {
  const hasValidSelection = selectedTools.length > 0;

  const handleToggleTool = (toolId: ScanTool) => {
    if (disabled) return;

    const isSelected = selectedTools.includes(toolId);

    // Prevent deselecting the last tool
    if (isSelected && selectedTools.length === 1) {
      return;
    }

    const newTools = isSelected
      ? selectedTools.filter(t => t !== toolId)
      : [...selectedTools, toolId];

    onChange(newTools);
  };

  return (
    <div>
      <label className="block text-body font-medium mb-3">Scanning Tools</label>
      <div className="space-y-2">
        {TOOLS.map(tool => {
          const isSelected = selectedTools.includes(tool.id);
          const isLastSelected = isSelected && selectedTools.length === 1;

          return (
            <label
              key={tool.id}
              className={`flex items-start gap-3 p-3 rounded-card border cursor-pointer transition-colors
                ${isSelected ? 'border-coral bg-coral/5' : 'border-gray-light hover:border-coral'}
                ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
                ${isLastSelected && !disabled ? 'cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggleTool(tool.id)}
                disabled={disabled || isLastSelected}
                className="mt-0.5 h-4 w-4 text-coral rounded focus:ring-coral disabled:opacity-50"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-caption font-medium ${tool.badgeColor} ${tool.badgeBgColor}`}
                  >
                    {tool.name}
                  </span>
                </div>
                <div className="text-body-sm text-gray-medium mt-1">{tool.description}</div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Validation message */}
      {!hasValidSelection && (
        <div className="flex items-center gap-2 mt-2 text-status-error text-body-sm">
          <AlertCircle size={14} />
          <span>At least one tool must be selected</span>
        </div>
      )}

      {/* Helper text */}
      <p className="text-caption text-gray-medium mt-2">
        Select the tools to use for scanning. Each tool provides different insights.
      </p>
    </div>
  );
}
